import { useMemo } from 'react';
import type { FinancialMetrics } from 'services/types/relifDashboard';
import { SPANISH_MONTH_NAMES } from 'utils/dateHelpers';

interface CostsByMonthDataPoint {
  month: string;
  label: string;
  ai: number;
  floid: number;
  meta: number;
  total: number;
}

interface UseFinancialDataReturn {
  contributionMarginData: Array<{ key: string; label: string; value: number }>;
  costsByMonthData: CostsByMonthDataPoint[];
  totalCosts: number;
}

export default function useFinancialData(financialData: FinancialMetrics | undefined): UseFinancialDataReturn {
  return useMemo(() => {
    if (!financialData) {
      return {
        contributionMarginData: [],
        costsByMonthData: [],
        totalCosts: 0,
      };
    }

    // Transform contribution margin data
    const contributionMarginData = financialData.contributionMargin?.byMonth
      ? Object.entries(financialData.contributionMargin.byMonth)
          .map(([key, value]) => {
            const monthNum = key.includes('-') ? parseInt(key.split('-')[1], 10) : 0;
            const monthLabel = monthNum > 0 && monthNum <= 12 ? SPANISH_MONTH_NAMES[monthNum - 1] : key;
            return {
              key,
              label: monthLabel,
              value: typeof value === 'number' ? value : 0,
            };
          })
          .sort((a, b) => a.key.localeCompare(b.key))
          .slice(-6)
      : [];

    // Transform costs by month data
    const costsByMonth = financialData.costsByMonth as
      | {
          ai: Record<string, number>;
          floid: Record<string, number>;
          meta: Record<string, number>;
        }
      | undefined;

    const allMonths = new Set<string>();
    if (costsByMonth?.ai) {
      Object.keys(costsByMonth.ai).forEach((m) => allMonths.add(m));
    }
    if (costsByMonth?.floid) {
      Object.keys(costsByMonth.floid).forEach((m) => allMonths.add(m));
    }
    if (costsByMonth?.meta) {
      Object.keys(costsByMonth.meta).forEach((m) => allMonths.add(m));
    }

    const sortedMonths = Array.from(allMonths).sort((a, b) => a.localeCompare(b));

    const costsByMonthData: CostsByMonthDataPoint[] = sortedMonths.map((month) => {
      const monthNum = parseInt(month.split('-')[1], 10);
      const monthLabel =
        monthNum > 0 && monthNum <= 12 ? SPANISH_MONTH_NAMES[monthNum - 1]?.slice(0, 3) || month : month;
      const ai = costsByMonth?.ai?.[month] || 0;
      const floid = costsByMonth?.floid?.[month] || 0;
      const meta = costsByMonth?.meta?.[month] || 0;
      return {
        month,
        label: monthLabel,
        ai,
        floid,
        meta,
        total: ai + floid + meta,
      };
    });

    // Calculate total costs
    const totalCosts =
      (financialData.costs?.ai || 0) + (financialData.costs?.floid || 0) + (financialData.costs?.meta || 0);

    return {
      contributionMarginData,
      costsByMonthData,
      totalCosts,
    };
  }, [financialData]);
}
